import { Pharmacy, useListFavoritePharmaciesQuery } from "@/App/api/pharmacy";
import { getPharmacyDescription } from "@/App/utils/utils";
import { Icon, LoadingGroup, LoadingSkeleton, Panel, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";
import { FC, useMemo } from "react";

interface PharmacyTileProps {
  readonly pharmacy: Pharmacy;
  readonly isLoading: boolean;
  readonly button?: JSX.Element;
  readonly userLocation: {
    readonly latitude: string;
    readonly longitude: string;
  };
  readonly index: number;
}

export const PharmacyTile: FC<PharmacyTileProps> = ({ pharmacy, isLoading, button, userLocation, index }) => {
  const { data: favoritePharmacies } = useListFavoritePharmaciesQuery();

  const isFavoritePharmacy = useMemo(() => {
    return (favoritePharmacies?.results || []).map((p) => p.pharmacy.provider_id).includes(pharmacy.provider_id);
  }, [favoritePharmacies, pharmacy]);

  const pharmacyDescription = useMemo(() => {
    return getPharmacyDescription(pharmacy);
  }, [pharmacy, userLocation]);

  const uncompressedView = useMemo(() => {
    return (
      <Shelf>
        <Stack spacing="mini">
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 36 }} />}>
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <Subheading testId={`pharmacy-${pharmacy.provider_id}-name`}>
                  {`${index + 1}. `}
                  {pharmacy?.name}
                </Subheading>
                {isFavoritePharmacy && <Icon testId={`pharmacy-${pharmacy.provider_id}-favorite-pharmacy`} name="star-filled" />}
                <Shelf.Spacer />
              </Shelf>
              <Shelf>
                {pharmacy.pharmacy_display_rules?.display_tag && (
                  <Tag testId={`pharmacy-${pharmacy.provider_id}-tag`} style={{ backgroundColor: "#313e48", color: "#f0f3f7" }}>
                    {pharmacy.pharmacy_display_rules.display_tag}
                  </Tag>
                )}
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%", height: 24 }} />}>
            <Body testId={`pharmacy-${pharmacy.provider_id}-description`}>{pharmacyDescription}</Body>
          </LoadingGroup.Placeholder>
        </Stack>
        {!!button && <Shelf.Spacer />}
        {!!button && button}
      </Shelf>
    );
  }, [isFavoritePharmacy, pharmacyDescription, pharmacy, isLoading, index, button, userLocation]);

  const compressedView = useMemo(() => {
    return (
      <Stack spacing="mini">
        <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 36 }} />}>
          <Shelf spacing="mini">
            <Subheading testId={`pharmacy-${pharmacy.provider_id}-name`}>
              {`${index + 1}. `}
              {pharmacy?.name}
            </Subheading>
            {isFavoritePharmacy && <Icon testId={`pharmacy-${pharmacy.provider_id}-favorite-pharmacy`} name="star-filled" />}
            <Shelf.Spacer />
          </Shelf>
        </LoadingGroup.Placeholder>
        <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%", height: 24 }} />}>
          <Shelf>
            {pharmacy.pharmacy_display_rules?.display_tag && (
              <Tag testId={`pharmacy-${pharmacy.provider_id}-tag`} style={{ backgroundColor: "#313e48", color: "#f0f3f7" }}>
                {pharmacy.pharmacy_display_rules.display_tag}
              </Tag>
            )}
            <Shelf.Spacer />
          </Shelf>
        </LoadingGroup.Placeholder>
        <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%", height: 24 }} />}>
          <Body testId={`pharmacy-${pharmacy.provider_id}-description`}>{pharmacyDescription}</Body>
        </LoadingGroup.Placeholder>
        {!!button && button}
      </Stack>
    );
  }, [isFavoritePharmacy, pharmacyDescription, pharmacy, isLoading, index, button, userLocation]);

  const { component } = useResponsive({
    component: {
      mobile: compressedView,
      desktop: uncompressedView,
    },
  });

  return (
    <LoadingGroup loading={isLoading}>
      <Panel>{component}</Panel>
    </LoadingGroup>
  );
};
